import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { join } from 'lodash';
import { useModelUpdate } from '@rhino-project/core/hooks';
import { Icon } from '@iconify/react';
import { Typography } from '@heroui/react';

type PlayerData = {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  number?: string | null;
};

type InlineEditHeadingProps = {
  player: PlayerData | null | undefined;
  isAdmin: boolean;
  onSaved?: () => void;
};

const InlineEditHeading = ({
  player,
  isAdmin,
  onSaved
}: InlineEditHeadingProps) => {
  const { t } = useTranslation();

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [number, setNumber] = useState('');
  const groupRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLInputElement>(null);
  const { mutate: updatePlayer } = useModelUpdate('player');

  const startEditing = useCallback(() => {
    if (!isAdmin) return;
    setFirstName(player?.first_name ?? '');
    setLastName(player?.last_name ?? '');
    setNumber(player?.number ?? '');
    setEditing(true);
  }, [isAdmin, player]);

  useEffect(() => {
    if (editing && firstRef.current) firstRef.current.focus();
  }, [editing]);

  const save = useCallback(() => {
    if (!lastName.trim()) {
      setLastName(player?.last_name ?? '');
      return;
    }

    const changes: Record<string, string | null | number> = {};
    if (firstName !== (player?.first_name ?? ''))
      changes.first_name = firstName || null;
    if (lastName !== (player?.last_name ?? '')) changes.last_name = lastName;
    if (number !== (player?.number ?? '')) changes.number = number || null;

    if (Object.keys(changes).length > 0) {
      updatePlayer(
        { id: player!.id, ...changes } as Parameters<typeof updatePlayer>[0],
        { onSuccess: onSaved }
      );
    }
    setEditing(false);
  }, [firstName, lastName, number, player, updatePlayer, onSaved]);

  const cancel = useCallback(() => {
    setEditing(false);
  }, []);

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (
        groupRef.current &&
        !groupRef.current.contains(document.activeElement)
      ) {
        save();
      }
    }, 0);
  }, [save]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') save();
      if (e.key === 'Escape') cancel();
    },
    [save, cancel]
  );

  if (editing) {
    return (
      <div ref={groupRef} className="flex items-center gap-2">
        <input
          ref={firstRef}
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={t('frontend.messages.first')}
          className="font-bold text-2xl bg-transparent border-b-2 border-accent/50 focus:border-accent outline-none w-32 py-0"
        />
        <input
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={t('frontend.messages.last')}
          className="font-bold text-2xl bg-transparent border-b-2 border-accent/50 focus:border-accent outline-none w-40 py-0"
        />
        <span className="text-2xl text-muted">#</span>
        <input
          type="text"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="—"
          maxLength={3}
          className="font-bold text-2xl bg-transparent border-b-2 border-accent/50 focus:border-accent outline-none w-12 py-0"
        />
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            save();
          }}
          className="p-1 text-success hover:text-success/80"
        >
          <Icon icon="bi:check-lg" width="20" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            cancel();
          }}
          className="p-1 text-muted hover:text-danger"
        >
          <Icon icon="bi:x-lg" width="18" />
        </button>
      </div>
    );
  }

  const displayName = join([player?.first_name, player?.last_name], ' ');
  const playerNumber = player?.number;

  return (
    <div
      className="group flex items-center gap-2 cursor-default"
      onClick={startEditing}
    >
      <Typography.Heading
        level={1}
        className={`m-0 ${isAdmin ? 'cursor-pointer' : ''}`}
      >
        {displayName} {playerNumber && `#${playerNumber}`}
      </Typography.Heading>
      {isAdmin && (
        <button
          type="button"
          className="p-1 text-muted opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity"
        >
          <Icon icon="bi:pencil" width="16" />
        </button>
      )}
    </div>
  );
};

export default InlineEditHeading;
